import {HttpClient} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {Translation, TranslocoLoader} from '@jsverse/transloco';
import {loadComponentTranslations} from '@covestro-cx/design-library-translations';
import {forkJoin, from, map} from 'rxjs';

/**
 * Converts a flat key-value map (e.g. {"footer.legal.heading": "Legal"})
 * into a nested object (e.g. {footer: {legal: {heading: "Legal"}}}).
 */
function unflatten(flat: Record<string, string>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(flat)) {
    const parts = key.split('.');
    let current: Record<string, unknown> = result;
    for (let i = 0; i < parts.length - 1; i++) {
      if (!current[parts[i]] || typeof current[parts[i]] !== 'object') {
        current[parts[i]] = {};
      }
      current = current[parts[i]] as Record<string, unknown>;
    }
    current[parts[parts.length - 1]] = value;
  }
  return result;
}

/**
 * Deep merges source into target. Source values override target values.
 */
function deepMerge(
  target: Record<string, unknown>,
  source: Record<string, unknown>,
): Record<string, unknown> {
  const output = {...target};
  for (const key of Object.keys(source)) {
    if (
      source[key] &&
      typeof source[key] === 'object' &&
      !Array.isArray(source[key]) &&
      target[key] &&
      typeof target[key] === 'object'
    ) {
      output[key] = deepMerge(
        target[key] as Record<string, unknown>,
        source[key] as Record<string, unknown>,
      );
    } else {
      output[key] = source[key];
    }
  }
  return output;
}

@Injectable({providedIn: 'root'})
export class TranslocoHttpLoader implements TranslocoLoader {
  private http = inject(HttpClient);

  /**
   * Loads and merges translations from two sources:
   * 1. App-specific translations from `/assets/i18n/{lang}.json` (nested format)
   * 2. Shared component translations from `@covestro-cx/design-library-translations` (flat format)
   *
   * Merge strategy: shared library translations override app translations (library-wins).
   * This ensures shared UI components (footer, topBar, etc.) always use the canonical
   * translations from the design library, preventing accidental app-level overrides.
   *
   * @param lang - The language code (e.g. "en", "de")
   * @returns Merged translation object for Transloco
   */
  getTranslation(lang: string) {
    return forkJoin({
      app: this.http.get<Translation>(`/assets/i18n/${lang}.json`),
      shared: from(loadComponentTranslations(lang)),
    }).pipe(
      map(({app, shared}) => {
        const nestedShared = unflatten(shared);
        // Library-wins: shared translations override app translations intentionally
        return deepMerge(app as Record<string, unknown>, nestedShared) as Translation;
      }),
    );
  }
}
